import React, { useMemo, useState } from 'react';
import '../styles/Organizers.css';
import BackgroundVectors from '../components/BackgroundVectors';
import TeamCard from '../components/TeamCardOrganizers';
import TwoColumnSection from '../components/TwoColumnSectionOrganizers';
import { TEAM_MEMBERS } from '../data/team';

const CATEGORIES = ['Leadership', 'IBM Team', 'Advisors', 'Organizing Team', 'IBM Qiskit Advocate Mentors'];

// IBM Team message — italic, same face as names (no quotes)
const IBM_TEAM_MESSAGE =
  'Thank you to the IBM Quantum Education team for providing technical support throughout the hackathon. A special thanks to IBM-ers Will Scherr, Sophy Shin, Brian Ingmanson, and Annaliese Estes for providing the technical content to Q^3 Qompute at USC!';

// ====== Leadership (static list) ======
const leadership = [
  {
    id: 'anjolie-tuazon',
    name: 'Anjolie Tuazon',
    role: 'Q^³ LEAD ORGANIZER',
    category: 'Leadership',
    campus: 'UMD',
    image: '/People/anjolie-tuazon.jpg',
    bio:
      'Anjolie Tuazon is currently the Co-Vice Chair for CQN’s Student and Postdoctoral Leadership Council and an undergraduate at the University of Maryland, College Park pursuing a degree in Electrical Engineering. Although she is often involved in heavy quantum research and extremely passionate about electronics, she has a hidden layer of artistic capability, working on video editing, illustration, and animation on her downtime.',
    linkedin: 'https://www.linkedin.com/in/anjolietuazon/',
  },
  {
    id: 'sandra-al-hames',
    name: 'Sandra Al Hames',
    role: 'LEAD ORGANIZER — Q^³ @ USC',
    category: 'Leadership',
    campus: 'USC',
    image: '/People/sandra-al-hames.jpg',
    bio:
      'Sandra Al Hames is currently the Chair of CQN’s Student and Postdoctoral Leadership Council and a student at the University of Southern California, double majoring in Astronautical Engineering and Mathematics. With two years of summer research in various CQN labs, Sandra discovered her passion at the intersection of ethics, policy, and science. She thrives in outreach initiatives aiming to engineer a more ethical future in emerging technology.',
    linkedin: 'https://www.linkedin.com/in/sandra-al-hames/',
  },
];

export default function Organizers() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const q = query.trim().toLowerCase();

  // Normalize members once
  const members = useMemo(
    () => (TEAM_MEMBERS || []).map((m) => ({ ...m, campus: m.campus || 'UMD' })),
    []
  );

  // Text search predicate (used for all sections)
  const matchesQuery = (m) =>
    !q ||
    (m.name && m.name.toLowerCase().includes(q)) ||
    (m.role && m.role.toLowerCase().includes(q)) ||
    (m.bio && m.bio.toLowerCase().includes(q));

  // Build section lists (independent of selected chip)
  const organizing = members.filter((m) => m.category === 'Organizing Team' && matchesQuery(m));
  const advisors   = members.filter((m) => m.category === 'Advisors' && matchesQuery(m));
  const ibm        = members.filter((m) => m.category === 'IBM Team' && matchesQuery(m));
  const qiskit     = members.filter((m) => m.category === 'IBM Qiskit Advocate Mentors' && matchesQuery(m));
  const leadershipFiltered = leadership.filter((m) => matchesQuery(m));

  // Pick which sections to render based on current chip
  let sections;
  switch (category) {
    case 'IBM Team':
      sections = [{ title: 'IBM Team', items: ibm }];
      break;
    case 'Advisors':
      sections = [{ title: 'Advisors', items: advisors }];
      break;
    case 'Organizing Team':
      sections = [{ title: 'Organizing Team', items: organizing }];
      break;
    case 'IBM Qiskit Advocate Mentors':
      sections = [{ title: 'IBM Qiskit Advocate Mentors', items: qiskit }];
      break;
    case 'Leadership':
      sections = []; // Leadership renders in its own block below when selected
      break;
    default: // 'All'
      sections = [
        { title: 'IBM Team', items: ibm },
        { title: 'Advisors', items: advisors },
        { title: 'Organizing Team', items: organizing },
        { title: 'IBM Qiskit Advocate Mentors', items: qiskit },
      ];
  }

  // Don’t render empty section headings (e.g., after search)
  sections = sections.filter((s) => s.items && s.items.length > 0);

  const showLeadership = (category === 'All' || category === 'Leadership') && leadershipFiltered.length > 0;
  const showIbmMessage = sections.some((s) => s.title === 'IBM Team');

  return (
    <div className="organizers-page">
      {/* background sets */}
      <BackgroundVectors />
      <BackgroundVectors shift={3000} />

      <main className="content-layer mx-auto max-w-6xl px-6 py-10">
        {/* Heading */}
        <div className="mb-12 mx-auto w-fit">
          <div className="flex items-start">
            <h1 className="about-prefix">About the</h1>
            <span className="about-organizers">Organizers</span>
          </div>
        </div>

        {/* CQN pill */}
        <div
          className="mx-auto mb-10 max-w-3xl rounded-[28px] px-6 py-6 text-white flex items-center gap-5 shadow-cqn"
          style={{ backgroundColor: '#0f3c5f' }}
        >
          <img
            src={process.env.PUBLIC_URL + '/cqn.png'}
            alt=""
            className="shrink-0"
            width="80"
            height="80"
          />
          <p className="cqn-text">
            The Center for Quantum Networks (CQN) is an NSF funded Engineering Research Center (ERC). Made of 12
            partnership institutions, CQN is building the Quantum Internet, developing technology to reliably carry
            information around the globe.
          </p>
        </div>

        {/* Filters + search */}
        <div className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
          <nav className="flex flex-wrap gap-2">
            {['All', ...CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={['filter-chip', category === c ? 'filter-chip--active' : ''].join(' ')}
              >
                {c}
              </button>
            ))}
          </nav>

          <div className="search-wrap w-full sm:w-96 relative block ml-auto">
            <input
              type="text"
              placeholder="Search by name, role, or keyword"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="search-input"
            />
            <span className="search-ico" aria-hidden>🔍</span>
          </div>
        </div>

        {/* Leadership — only when All or Leadership is selected */}
        {showLeadership && (
          <section className="mt-10">
            <h2 className="section-title">Leadership</h2>
            <div className="two-col">
              {leadershipFiltered.map((m) => (
                <div key={m.id} className="leader-col">
                  <TeamCard member={m} frameless hideSocial />
                  {m.bio && <p className="leader-bio">{m.bio}</p>}
                  {m.linkedin && (
                    <div className="leader-social">
                      <a
                        className="social-btn"
                        href={m.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${m.name} on LinkedIn`}
                        title="LinkedIn"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM.5 8h4v16h-4zM8 8h3.8v2.2h.05C12.38 9.2 13.67 8 15.6 8 19.61 8 20.35 10.64 20.35 14.08V24h-4v-7.1c0-1.69-.03-3.87-2.36-3.87-2.36 0-2.73 1.84-2.73 3.75V24H8V8z" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="double-rule"><span></span><span></span></div>
          </section>
        )}

        {/* Other sections — only the one(s) selected */}
        {sections.length > 0 && (
          <TwoColumnSection
            sections={sections}
            TeamCard={TeamCard}
            ibmMessage={showIbmMessage ? IBM_TEAM_MESSAGE : undefined}
          />
        )}
      </main>
    </div>
  );
}
